import { useState } from 'react'
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
} from '@mui/material'
import {
  Upload,
  Download,
  Visibility,
  Delete,
  Description,
} from '@mui/icons-material'
import { useAppSelector } from '../hooks/redux'
import { useGetMemorialsQuery, useSubmitMemorialMutation, useLazyDownloadMemorialQuery, useDeleteMemorialMutation } from '../store/api/memorialsApi'
import { useGetCasesQuery } from '../store/api/casesApi'
import { useGetTeamsQuery } from '../store/api/teamsApi'
import { hasPermission } from '../utils/permissions'
import { formatCaseDisplay } from '../utils/caseDisplay'

export default function MemorialsPage() {
  const { user: currentUser } = useAppSelector((state) => state.auth)
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false)
  const [selectedCaseId, setSelectedCaseId] = useState<string>('')
  
  const { data: cases = [] } = useGetCasesQuery()
  const { data: teams = [], isLoading: isLoadingTeams } = useGetTeamsQuery()
  const { data: memorials = [], isLoading, refetch } = useGetMemorialsQuery(
    { case_id: selectedCaseId || undefined },
    { skip: false }
  )
  
  const [submitMemorial, { isLoading: isSubmitting }] = useSubmitMemorialMutation()
  const [downloadMemorial] = useLazyDownloadMemorialQuery()
  const [deleteMemorial] = useDeleteMemorialMutation()
  
  // Snackbar state
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: 'success' | 'error' | 'warning' | 'info'
  }>({
    open: false,
    message: '',
    severity: 'success',
  })
  
  // View memorial dialog state
  const [viewDialogOpen, setViewDialogOpen] = useState(false)
  const [viewingMemorial, setViewingMemorial] = useState<any>(null)

  // Get user's team
  const userTeam = teams.find(
    (t) => t.members.includes(currentUser?.id || '') || t.coach_id === currentUser?.id
  )

  // Filter cases assigned to user's team
  const userCases = cases.filter((c) => {
    if (!userTeam) return false
    return c.assigned_team_ids?.includes(userTeam.id)
  })

  const canSubmitMemorial = currentUser && hasPermission(currentUser.role, 'SUBMIT_MEMORIAL')

  // Upload form state
  const [uploadForm, setUploadForm] = useState({
    case_id: '',
    team_id: userTeam?.id || '',
    file: null as File | null,
    title: '',
    version: 1,
  })

  const handleOpenUploadDialog = () => {
    if (userCases.length > 0) {
      const selectedCase = userCases[0]
      // Check deadline
      if (selectedCase.deadline) {
        const deadline = new Date(selectedCase.deadline)
        const now = new Date()
        if (now > deadline) {
          setSnackbar({
            open: true,
            message: `Đã quá deadline nộp bài: ${deadline.toLocaleString('vi-VN')}`,
            severity: 'error',
          })
          return
        }
      }
      setUploadForm({
        ...uploadForm,
        case_id: userCases[0].id,
        team_id: userTeam?.id || '',
      })
    }
    setUploadDialogOpen(true)
  }

  const handleCloseUploadDialog = () => {
    setUploadDialogOpen(false)
    setUploadForm({
      case_id: '',
      team_id: userTeam?.id || '',
      file: null,
      title: '',
      version: 1,
    })
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setUploadForm({ ...uploadForm, file: event.target.files[0] })
    }
  }

  const handleSubmit = async () => {
    if (!uploadForm.file || !uploadForm.case_id || !uploadForm.team_id) {
      setSnackbar({
        open: true,
        message: 'Vui lòng điền đầy đủ thông tin',
        severity: 'error',
      })
      return
    }

    // Check deadline
    const selectedCase = cases.find((c) => c.id === uploadForm.case_id)
    if (selectedCase?.deadline) {
      const deadline = new Date(selectedCase.deadline)
      const now = new Date()
      if (now > deadline) {
        setSnackbar({
          open: true,
          message: `Đã quá deadline nộp bài: ${deadline.toLocaleString('vi-VN')}`,
          severity: 'error',
        })
        return
      }
    }

    const formData = new FormData()
    formData.append('file', uploadForm.file)
    formData.append('case_id', uploadForm.case_id)
    formData.append('team_id', uploadForm.team_id)
    if (uploadForm.title) formData.append('title', uploadForm.title)
    formData.append('version', uploadForm.version.toString())

    try {
      await submitMemorial(formData).unwrap()
      handleCloseUploadDialog()
      refetch() // Refetch danh sách memorials
      setSnackbar({
        open: true,
        message: 'Nộp memorial thành công!',
        severity: 'success',
      })
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: `Nộp memorial thất bại: ${error.data?.detail || error.message}`,
        severity: 'error',
      })
    }
  }

  const handleDownload = async (memorialId: string, fileName: string) => {
    try {
      const result = await downloadMemorial(memorialId).unwrap()
      const url = window.URL.createObjectURL(result)
      const a = document.createElement('a')
      a.href = url
      a.download = fileName
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
      setSnackbar({
        open: true,
        message: 'Download thành công!',
        severity: 'success',
      })
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: `Download thất bại: ${error.data?.detail || error.message}`,
        severity: 'error',
      })
    }
  }
  
  const handleViewMemorial = (memorial: any) => {
    setViewingMemorial(memorial)
    setViewDialogOpen(true)
  }
  
  const handleCloseViewDialog = () => {
    setViewDialogOpen(false)
    setViewingMemorial(null)
  }

  // Delete memorial handler
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [memorialToDelete, setMemorialToDelete] = useState<any>(null)

  const handleDeleteClick = (memorial: any) => {
    setMemorialToDelete(memorial)
    setDeleteConfirmOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!memorialToDelete) return

    try {
      await deleteMemorial(memorialToDelete.id).unwrap()
      setSnackbar({
        open: true,
        message: 'Xóa memorial thành công!',
        severity: 'success',
      })
      refetch()
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: `Xóa memorial thất bại: ${error.data?.detail || error.message}`,
        severity: 'error',
      })
    } finally {
      setDeleteConfirmOpen(false)
      setMemorialToDelete(null)
    }
  }

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false)
    setMemorialToDelete(null)
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'submitted':
        return 'success'
      case 'draft':
        return 'default'
      default:
        return 'info'
    }
  }

  return (
    <Container maxWidth="xl" sx={{ mt: { xs: 2, sm: 4 }, mb: { xs: 2, sm: 4 }, px: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 2, sm: 3 }, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 2, sm: 0 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Description sx={{ fontSize: { xs: 32, sm: 40 }, color: 'primary.main' }} />
          <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
            Quản lý Memorial
          </Typography>
        </Box>
        {canSubmitMemorial && userTeam && (
          <Button
            variant="contained"
            startIcon={<Upload />}
            onClick={handleOpenUploadDialog}
            disabled={userCases.length === 0}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              borderRadius: 1,
              px: 3,
              py: 1,
            }}
          >
            Nộp Memorial
          </Button>
        )}
      </Box>

      {!canSubmitMemorial && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Chỉ sinh viên (students) mới có quyền nộp memorial cho case được assign cho đội của mình.
        </Alert>
      )}

      {canSubmitMemorial && userCases.length === 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Bạn chưa có case nào được assign cho đội của mình.
        </Alert>
      )}

      {/* Filter */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <FormControl fullWidth>
          <InputLabel>Lọc theo cuộc thi</InputLabel>
          <Select
            value={selectedCaseId}
            label="Lọc theo cuộc thi"
            onChange={(e) => setSelectedCaseId(e.target.value)}
          >
            <MenuItem value="">Tất cả</MenuItem>
            {userCases.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {formatCaseDisplay(c)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>

      {/* Memorials Table */}
      <Paper elevation={3} sx={{ borderRadius: { xs: 1, sm: 2 } }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Cuộc thi</TableCell>
                <TableCell>Đội thi</TableCell>
                <TableCell>File</TableCell>
                <TableCell>Kích thước</TableCell>
                <TableCell>Version</TableCell>
                <TableCell>Trạng thái</TableCell>
                <TableCell>Ngày nộp</TableCell>
                <TableCell align="right">Thao tác</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading || isLoadingTeams ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : memorials.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="text.secondary">
                      Chưa có memorial nào
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                memorials.map((memorial) => {
                  const caseItem = cases.find((c) => c.id === memorial.case_id)
                  const team = teams.find((t) => t.id === memorial.team_id)
                  // Đảm bảo hiển thị tên đội, nếu không tìm thấy thì hiển thị ID
                  const teamName = team ? team.name : memorial.team_id
                  return (
                    <TableRow key={memorial.id}>
                      <TableCell>{caseItem ? formatCaseDisplay(caseItem) : memorial.case_id}</TableCell>
                      <TableCell>{teamName}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Description />
                          <Typography variant="body2">{memorial.file_name}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{(memorial.file_size / 1024 / 1024).toFixed(2)} MB</TableCell>
                      <TableCell>{memorial.version || 1}</TableCell>
                      <TableCell>
                        <Chip
                          label={memorial.status}
                          color={getStatusColor(memorial.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {memorial.submitted_at
                          ? new Date(memorial.submitted_at).toLocaleString('vi-VN')
                          : '-'}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleDownload(memorial.id, memorial.file_name)}
                          title="Download"
                        >
                          <Download />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          title="Xem chi tiết"
                          onClick={() => handleViewMemorial(memorial)}
                        >
                          <Visibility />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteClick(memorial)}
                          title="Xóa"
                          color="error"
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Upload Dialog */}
      <Dialog 
        open={uploadDialogOpen} 
        onClose={handleCloseUploadDialog} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: { xs: 1, sm: 2 },
            m: { xs: 1, sm: 2 },
          }
        }}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' }, fontWeight: 600 }}>
          Nộp Memorial
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: { xs: 1, sm: 2 } }}>
            <FormControl fullWidth required>
              <InputLabel>Cuộc thi *</InputLabel>
              <Select
                value={uploadForm.case_id}
                label="Cuộc thi *"
                onChange={(e) => setUploadForm({ ...uploadForm, case_id: e.target.value })}
                required
              >
                {userCases.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {formatCaseDisplay(c)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Tiêu đề (tùy chọn)"
              value={uploadForm.title}
              onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
            />
            <TextField
              fullWidth
              type="number"
              label="Version"
              value={uploadForm.version}
              onChange={(e) => setUploadForm({ ...uploadForm, version: parseInt(e.target.value) || 1 })}
              inputProps={{ min: 1 }}
            />
            <Button variant="outlined" component="label" fullWidth startIcon={<Upload />}>
              Chọn File (PDF hoặc DOCX)
              <input type="file" hidden accept=".pdf,.docx" onChange={handleFileChange} />
            </Button>
            {uploadForm.file && (
              <Typography variant="body2">
                Đã chọn: {uploadForm.file.name} ({(uploadForm.file.size / 1024 / 1024).toFixed(2)} MB)
              </Typography>
            )}
            {uploadForm.case_id && (() => {
              const selectedCase = cases.find((c) => c.id === uploadForm.case_id)
              if (selectedCase?.deadline) {
                const deadline = new Date(selectedCase.deadline)
                const now = new Date()
                const isOverdue = now > deadline
                return (
                  <Alert severity={isOverdue ? 'error' : 'info'}>
                    Deadline nộp bài: {deadline.toLocaleString('vi-VN')}
                    {isOverdue && ' (Đã quá deadline)'}
                  </Alert>
                )
              }
              return null
            })()}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, gap: 1 }}>
          <Button 
            onClick={handleCloseUploadDialog}
            sx={{ textTransform: 'none', fontWeight: 500 }}
          >
            Hủy
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            disabled={isSubmitting}
            sx={{ textTransform: 'none', fontWeight: 500, borderRadius: 1 }}
          >
            {isSubmitting ? <CircularProgress size={20} /> : 'Nộp Memorial'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Memorial Dialog */}
      <Dialog 
        open={viewDialogOpen} 
        onClose={handleCloseViewDialog} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: { xs: 1, sm: 2 },
            m: { xs: 1, sm: 2 },
          }
        }}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' }, fontWeight: 600 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Description />
            <Typography variant="h6">Chi tiết Memorial</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
          {viewingMemorial && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Tiêu đề
                </Typography>
                <Typography variant="body1">{viewingMemorial.title || viewingMemorial.file_name}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Cuộc thi
                </Typography>
                <Typography variant="body1">
                  {cases.find((c) => c.id === viewingMemorial.case_id) 
                    ? formatCaseDisplay(cases.find((c) => c.id === viewingMemorial.case_id)!)
                    : viewingMemorial.case_id}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Đội thi
                </Typography>
                <Typography variant="body1">
                  {(() => {
                    const team = teams.find((t) => t.id === viewingMemorial.team_id)
                    return team ? team.name : (isLoadingTeams ? 'Đang tải...' : viewingMemorial.team_id)
                  })()}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  File
                </Typography>
                <Typography variant="body1">{viewingMemorial.file_name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Kích thước: {(viewingMemorial.file_size / 1024 / 1024).toFixed(2)} MB
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Version
                </Typography>
                <Chip label={viewingMemorial.version || 1} size="small" />
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Trạng thái
                </Typography>
                <Chip
                  label={viewingMemorial.status}
                  color={getStatusColor(viewingMemorial.status)}
                  size="small"
                />
              </Box>
              {viewingMemorial.submitted_at && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Ngày nộp
                  </Typography>
                  <Typography variant="body1">
                    {new Date(viewingMemorial.submitted_at).toLocaleString('vi-VN')}
                  </Typography>
                </Box>
              )}
              {viewingMemorial.tags && viewingMemorial.tags.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Tags
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {viewingMemorial.tags
                      .filter((tag: string) => {
                        // Bỏ qua team_id và case_id nếu đã có team: và case:
                        if (tag.startsWith('team_id:') && viewingMemorial.tags.some((t: string) => t.startsWith('team:'))) {
                          return false
                        }
                        if (tag.startsWith('case_id:') && viewingMemorial.tags.some((t: string) => t.startsWith('case:'))) {
                          return false
                        }
                        return true
                      })
                      .map((tag: string, idx: number) => {
                        // Format tag để hiển thị tên thay vì ID
                        let displayTag = tag
                        if (tag.startsWith('team:')) {
                          displayTag = tag.replace('team:', 'Đội: ')
                        } else if (tag.startsWith('case:')) {
                          displayTag = tag.replace('case:', 'Cuộc thi: ')
                        } else if (tag.startsWith('case_id:')) {
                          const caseId = tag.replace('case_id:', '')
                          const caseItem = cases.find((c) => c.id === caseId)
                          displayTag = caseItem ? `Cuộc thi: ${formatCaseDisplay(caseItem)}` : tag
                        } else if (tag.startsWith('team_id:')) {
                          const teamId = tag.replace('team_id:', '')
                          const team = teams.find((t) => t.id === teamId)
                          displayTag = team ? `Đội: ${team.name}` : tag
                        } else if (tag.startsWith('case_number:')) {
                          displayTag = tag.replace('case_number:', 'Số vụ án: ')
                        }
                        return (
                          <Chip key={idx} label={displayTag} size="small" variant="outlined" />
                        )
                      })}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {viewingMemorial && (
            <Button
              startIcon={<Download />}
              onClick={() => handleDownload(viewingMemorial.id, viewingMemorial.file_name)}
            >
              Download
            </Button>
          )}
          <Button onClick={handleCloseViewDialog}>Đóng</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog 
        open={deleteConfirmOpen} 
        onClose={handleDeleteCancel}
        PaperProps={{
          sx: {
            borderRadius: { xs: 1, sm: 2 },
            m: { xs: 1, sm: 2 },
          }
        }}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' }, fontWeight: 600 }}>
          Xác nhận xóa
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
          <Typography>
            Bạn có chắc muốn xóa memorial "{memorialToDelete?.title || memorialToDelete?.file_name}"?
            Hành động này không thể hoàn tác.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, gap: 1 }}>
          <Button 
            onClick={handleDeleteCancel}
            sx={{ textTransform: 'none', fontWeight: 500 }}
          >
            Hủy
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            variant="contained"
            sx={{ textTransform: 'none', fontWeight: 500, borderRadius: 1 }}
          >
            Xóa
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  )
}
